import { formatDateToFEFormatDateFns } from 'utils/date'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'

// Helper: empty check
const isEmpty = v => v === null || v === undefined || v === ''

// Helper: bool formatting
const formatBool = v => (v ? 'Yes' : 'No')

// Helper: date formatting to FE timezone (date | time like Configured Service Activity)
const formatDateTime = iso => {
  if (!iso) return ''
  try {
    const { onlyDate, onlyTime } = convertDateTimeToPreferedTimezone({ dateTime: iso })
    if (onlyDate || onlyTime) {
      return [onlyDate, onlyTime].filter(Boolean).join(' | ')
    }
  } catch (e) {}
  try {
    return formatDateToFEFormatDateFns(new Date(iso))
  } catch (_) {
    return String(iso)
  }
}

// Helper: meta extraction from a generic version object (updatedByUser/createdByUser, updatedAt/createdAt)
export const extractUserMeta = version => {
  const by = version?.updatedByUser || version?.createdByUser || {}
  const name = [by.firstName, by.lastName].filter(Boolean).join(' ').trim() || by.username || ''
  return {
    modifiedBy: name,
    modifiedByName: name,
    modifiedByUsername: by.username || '',
    modifiedAt: formatDateTime(version?.updatedAt || version?.createdAt || ''),
  }
}

// Helper: change type based on values
export const toChangeTypeFromValues = (prevVal, currVal) => {
  const prevEmpty = isEmpty(prevVal)
  const currEmpty = isEmpty(currVal)
  if (prevEmpty && !currEmpty) return 'Added'
  if (!prevEmpty && currEmpty) return 'Deleted'
  return 'Updated'
}

/**
 * Build a tree of activity rows from entity versions using a declarative fields config.
 *
 * @param {Array<Object>} versions - Array of version snapshots.
 * @param {Object} config
 * @param {string} [config.idPrefix] - Prefix for generated row ids (default: 'rev').
 * @param {(version: Object) => Object} [config.getMeta] - Function to extract meta like modifiedBy/At; default uses extractUserMeta.
 * @param {Array<{ key: string, label: string, getValue: (version: Object)=>any, format?: (value:any)=>string }>} config.fields
 *        Each field describes how to read and optionally format a value from a version snapshot.
 *
 * Strict diff rule: Only output a parent group when at least one tracked field changed between consecutive versions.
 */
export default function buildVersionActivityTree(versions = [], config = {}) {
  const { idPrefix = 'rev', getMeta = extractUserMeta, fields = [] } = config
  if (!Array.isArray(versions) || versions.length === 0 || fields.length === 0) return []

  // Sort by rev desc (fallback to updatedAt desc)
  const sorted = [...versions].sort((a, b) => {
    const ar = a?.rev ?? 0
    const br = b?.rev ?? 0
    if (ar !== br) return br - ar
    const ad = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0
    const bd = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0
    return bd - ad
  })

  const rows = []

  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i] || {}
    const base = sorted[i + 1] || {}

    const rev = current.rev ?? i
    const parentId = `${idPrefix}-${rev}`
    const meta = getMeta(current)

    // Build child rows first
    const childRows = []

    for (let fIdx = 0; fIdx < fields.length; fIdx++) {
      const field = fields[fIdx]
      const label = field.label
      const currRaw = field.getValue ? field.getValue(current) : current[field.key]
      const prevRaw = field.getValue ? field.getValue(base) : base?.[field.key]

      const bothEmpty = isEmpty(prevRaw) && isEmpty(currRaw)
      const equal = prevRaw === currRaw
      if (bothEmpty || equal) continue

      let oldValue = prevRaw
      let newValue = currRaw

      // formatting
      const currIsBool = typeof currRaw === 'boolean'
      const prevIsBool = typeof prevRaw === 'boolean'
      if (currIsBool || prevIsBool) {
        if (prevIsBool) oldValue = formatBool(prevRaw)
        if (currIsBool) newValue = formatBool(currRaw)
      }
      if (field.format) {
        oldValue = isEmpty(oldValue) ? oldValue : field.format(oldValue)
        newValue = isEmpty(newValue) ? newValue : field.format(newValue)
      }
      if (isEmpty(oldValue)) oldValue = 'None'
      if (isEmpty(newValue)) newValue = 'None'

      const childId = `${parentId}-${field.key}-${fIdx}`
      childRows.push({
        id: childId,
        hierarchy: [parentId, childId],
        field: label,
        fieldKey: field.key,
        oldValue,
        newValue,
        changeType: toChangeTypeFromValues(prevRaw, currRaw),
        rev,
        ...meta,
      })
    }

    if (childRows.length === 0) continue

    // Push parent row then children (children already have hierarchy)
    rows.push({
      id: parentId,
      hierarchy: [parentId],
      rev,
      changeType: 'Batch',
      ...meta,
    })
    rows.push(
      ...childRows.sort((a, b) => (a.field || '').localeCompare(b.field || '', undefined, { sensitivity: 'base' }))
    )
  }

  return rows
}
